import type { BatMetadataConfigEntry } from './common/types';
import { coreFields } from './common/coreFields';
import { vendorFields } from './common/vendorFields';
import { msg } from '@lingui/core/macro';
import {
  productFields,
  purchasedMaterialFields,
  purchasedMaterialIdField,
  purchasedMaterialNameField,
} from './common/productFields';
import {
  currencyField,
  currencyQuantityField,
  financialAccountIdField,
  financialAccountNameField,
} from './common/expenditureFields';
import { genericQuantityField, genericUnitField } from './common/sharedFields';
import { locationFields } from './common/locationFields';

export const purchaseOrdersMetadata = {
  displayName: msg({
    message: 'Purchase orders',
    context: 'Activity data schema name for purchase orders',
  }),
  fields: {
    ...coreFields,
    ...vendorFields,
    ...productFields,
    ...purchasedMaterialFields,
    ...locationFields,
    country_of_origin: {
      displayName: msg({
        message: 'Country of origin',
        context: 'The display name for a dimension',
      }),
    },
    currency: currencyField,
    currency_quantity: currencyQuantityField,
    financial_account_id: financialAccountIdField,
    financial_account_name: financialAccountNameField,
    material_variant_identifier_override: {
      displayName: msg({
        message: 'Material variant identifier override',
        context:
          'The display name for the material variant identifier override field',
      }),
    },
    material_variant_lineage_id: {
      displayName: msg({
        message: 'Material variant lineage ID',
        context: 'The display name for the material variant lineage ID field',
      }),
    },
    purchase_order_id: {
      displayName: msg({
        message: 'Purchase order ID',
        context: 'The display name for the purchase order ID field',
      }),
    },
    purchased_material_external_id: purchasedMaterialIdField,
    purchased_material_group_l1: {
      displayName: msg({
        message: 'Purchased material group L1',
        context: 'The display name for the purchased material group L1 field',
      }),
    },
    purchased_material_group_l2: {
      displayName: msg({
        message: 'Purchased material group L2',
        context: 'The display name for the purchased material group L2 field',
      }),
    },
    purchased_material_group_l3: {
      displayName: msg({
        message: 'Purchased material group L3',
        context: 'The display name for the purchased material group L3 field',
      }),
    },
    purchased_material_name: purchasedMaterialNameField,
    purchased_material_variant_name: {
      displayName: msg({
        message: 'Purchased material variant name',
        context:
          'The display name for the purchased material variant name field',
      }),
    },
    quantity: genericQuantityField,
    quantity_standardized: {
      displayName: msg({
        message: 'Standardized quantity',
        context: 'The display name for the standardized quantity field',
      }),
    },
    receiving_facility_id: {
      displayName: msg({
        message: 'Receiving facility external ID',
        context:
          'The display name for the receiving facility external ID field',
      }),
    },
    receiving_facility_location_city: {
      displayName: msg({
        message: 'Receiving facility location city',
        context:
          'The display name for the receiving facility location city field',
      }),
    },
    receiving_facility_location_country: {
      displayName: msg({
        message: 'Receiving facility location country',
        context:
          'The display name for the receiving facility location country field',
      }),
    },
    receiving_facility_location_postal_code: {
      displayName: msg({
        message: 'Receiving facility location postal code',
        context:
          'The display name for the receiving facility location postal code field',
      }),
    },
    receiving_facility_location_street_address: {
      displayName: msg({
        message: 'Receiving facility location street address',
        context:
          'The display name for the receiving facility location street address field',
      }),
    },
    receiving_facility_location_street_address_2: {
      displayName: msg({
        message: 'Receiving facility location street address 2',
        context:
          'The display name for the receiving facility location street address 2 field',
      }),
    },
    receiving_facility_location_subdivision: {
      displayName: msg({
        message: 'Receiving facility location subdivision',
        context:
          'The display name for the receiving facility location subdivision field',
      }),
    },
    receiving_raw_vendor_entity: {
      displayName: msg({
        message: 'Receiving supplier - Imported',
        context: 'The display name for the receiving supplier - Imported field',
      }),
    },
    receiving_tier: {
      displayName: msg({
        message: 'Receiving tier',
        context: 'The display name for the receiving tier field',
      }),
    },
    receiving_vendor_external_id: {
      displayName: msg({
        message: 'Receiving supplier external ID',
        context:
          'The display name for the receiving supplier external ID field',
      }),
    },
    sending_facility_id: {
      displayName: msg({
        message: 'Sending facility external ID',
        context: 'The display name for the sending facility external ID field',
      }),
    },
    sending_facility_location_city: {
      displayName: msg({
        message: 'Sending facility location city',
        context:
          'The display name for the sending facility location city field',
      }),
    },
    sending_facility_location_country: {
      displayName: msg({
        message: 'Sending facility location country',
        context:
          'The display name for the sending facility location country field',
      }),
    },
    sending_facility_location_postal_code: {
      displayName: msg({
        message: 'Sending facility location postal code',
        context:
          'The display name for the sending facility location postal code field',
      }),
    },
    sending_facility_location_street_address: {
      displayName: msg({
        message: 'Sending facility location street address',
        context:
          'The display name for the sending facility location street address field',
      }),
    },
    sending_facility_location_street_address_2: {
      displayName: msg({
        message: 'Sending facility location street address 2',
        context:
          'The display name for the sending facility location street address 2 field',
      }),
    },
    sending_facility_location_subdivision: {
      displayName: msg({
        message: 'Sending facility location subdivision',
        context:
          'The display name for the sending facility location subdivision field',
      }),
    },
    sending_raw_vendor_entity: {
      displayName: msg({
        message: 'Sending supplier - Imported',
        context: 'The display name for the sending supplier - Imported field',
      }),
    },
    sending_tier: {
      displayName: msg({
        message: 'Sending tier',
        context: 'The display name for the sending tier field',
      }),
    },
    sending_vendor_external_id: {
      displayName: msg({
        message: 'Sending supplier external ID',
        context: 'The display name for the sending supplier external ID field',
      }),
    },
    unit: genericUnitField,
    unit_standardized: {
      displayName: msg({
        message: 'Standardized unit',
        context: 'The display name for the standardized unit field',
      }),
    },
  },
} as const satisfies BatMetadataConfigEntry;
